import React from 'react'
import { Sidebar, Text, Heading, Input } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { supabase } from '../../lib/supabase'
import { crmService } from '../../services/crm'
import { CRMStage } from '../../types/crm'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { CRMSearchItem } from '../../types/crm'

const CRMDashboard: React.FC = () => {
  const [stages, setStages] = React.useState<CRMStage[]>([])
  const [metrics, setMetrics] = React.useState<Array<{ stage_id: string; stage_name: string; count_opps: number; sum_value_cents: number }>>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [search, setSearch] = React.useState<string>('')
  const [searching, setSearching] = React.useState<boolean>(false)
  const [results, setResults] = React.useState<CRMSearchItem[]>([])

  React.useEffect(() => {
    const load = async () => {
      setLoading(true)
      const s = await crmService.getStages()
      const { data } = await supabase.rpc('crm_stage_metrics', {} as any)
      setStages(s)
      setMetrics((data || []) as any)
      setLoading(false)
    }
    load()
  }, [])

  const totalWon = React.useMemo(() => {
    const wonIds = new Set(stages.filter(s => s.is_won).map(s => s.id))
    const cents = metrics.filter(m => wonIds.has(m.stage_id)).reduce((acc, m) => acc + (m.sum_value_cents || 0), 0)
    return cents / 100
  }, [stages, metrics])

  const totalOpps = React.useMemo(() => metrics.reduce((acc, m) => acc + (m.count_opps || 0), 0), [metrics])

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName="crm" permissionType="show">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">CRM</Heading>
              <Text size="sm" color="muted" className="mt-2">Resumo do funil, vendas e produtividade</Text>
            </div>
            {/* Busca global */}
            <div className="mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Input
                    placeholder="Buscar em Contatos, Empresas e Oportunidades..."
                    value={search}
                    onChange={async (e: any) => {
                      const term = e.target.value
                      setSearch(term)
                      if (!term || term.length < 2) {
                        setResults([])
                        return
                      }
                      setSearching(true)
                      try {
                        const list = await crmService.search(term)
                        setResults(list)
                      } finally {
                        setSearching(false)
                      }
                    }}
                  />
                </div>
              </div>
              {searching && <Text size="sm" color="muted" className="mt-2">Buscando...</Text>}
              {!searching && results.length > 0 && (
                <div className="mt-3 bg-white dark:bg-gray-800 rounded-md shadow border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {results.map((r) => (
                    <div key={`${r.entity_type}_${r.entity_id}`} className="px-4 py-2 text-sm">
                      <span className="text-gray-500 dark:text-gray-400 mr-2 uppercase">{r.entity_type}</span>
                      <span className="text-gray-900 dark:text-white">{r.title}</span>
                      {r.subtitle && <span className="text-gray-500 dark:text-gray-400 ml-2">• {r.subtitle}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {loading ? (
              <Text size="sm" color="muted">Carregando...</Text>
            ) : (
              <>
                {/* KPIs */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-5 text-white">
                    <div className="text-xs opacity-80">Vendas (ganhas)</div>
                    <div className="text-2xl font-semibold">R$ {totalWon.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-5 text-white">
                    <div className="text-xs opacity-80">Oportunidades</div>
                    <div className="text-2xl font-semibold">{totalOpps}</div>
                  </div>
                </div>

                {/* Gráfico por estágio (valor) */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
                  <div className="flex items-center justify-between mb-4">
                    <Text size="lg" className="text-gray-900 dark:text-white">Valor por estágio</Text>
                    <Text size="sm" color="muted">Total em R$</Text>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={metrics.map(m => ({ name: m.stage_name, valor: (m.sum_value_cents || 0) / 100 }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="valor" fill="#008B95" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default CRMDashboard
